/**
 * What the section says for a spark-esign refusal (the Mailbox `errors.ts` shape). The copy is
 * chosen by `code` (the umbrella spec's Copy table); the service's `message` is for its logs and is
 * never shown. The silent codes (`SILENT_CODES`, Decision 10) say nothing: the section refetches and
 * the current state says it. Every line is translated here and handed to Callout as its `text`
 * (Decision 9).
 */
import type { TFunction } from 'i18next';

import type { CalloutTone } from '../../components';
import {
  isContractError,
  OPERATOR_NAME_MAX,
  SIGNATORY_NAME_MAX,
  SILENT_CODES,
  type ContractError,
  type ContractErrorCode,
} from './api/contract';
import type { NameErrorCode, NameErrors } from './form';
import type { Notice } from './state';

/** The service's error behind a rejection, or null when the rejection was not the service answering
 *  (not configured, the network, a malformed body): the generic line then. */
export const takeContractError = (rejection: unknown): ContractError | null =>
  isContractError(rejection) ? rejection : null;

const popupField = (field: unknown): keyof NameErrors | null =>
  field === 'operator_name' ? 'operator' : field === 'signatory_name' ? 'signatory' : null;

const isNameErrorCode = (code: unknown): code is NameErrorCode => code === 'required' || code === 'too_long';

/** The service's VALIDATION `fields` folded onto the two popup fields (operator_name -> operator,
 *  signatory_name -> signatory). A field the popup does not have is left out. */
export const fieldErrorsOf = (error: ContractError | null): NameErrors => {
  const errors: NameErrors = {};
  if (error === null || error.code !== 'VALIDATION' || error.fields === null) return errors;
  for (const entry of error.fields) {
    // `isContractError` checks that `fields` is a list, not each entry: read an entry defensively.
    const field = popupField(entry?.field);
    if (field !== null && errors[field] === undefined && isNameErrorCode(entry.code)) errors[field] = entry.code;
  }
  return errors;
};

/** The TRANSLATED copy for a notice, or null for a silent code or a VALIDATION with usable fields.
 *  Rendered as Callout's `text` (Decision 9). */
export const contractErrorText = ({ error }: Notice, t: TFunction): string | null => {
  if (error !== null) {
    if (SILENT_CODES.has(error.code)) return null;
    switch (error.code) {
      case 'RESEND_TOO_SOON':
        return t('You can resend the contract again in a few minutes.');
      case 'DAILY_LIMIT':
        return t('You can start again tomorrow.');
      case 'DOCUMENT_PENDING':
        return t('Your signed copy is being prepared. Try again in a minute.');
      case 'BRAND_NAME_TOO_LONG':
        return t('Your brand name is too long for the contract. Contact support.');
      case 'DOMAIN_TOO_LONG':
        return t('Your domain is too long for the contract. Contact support.');
      case 'EMAIL_TOO_LONG':
        return t('Your email address is too long for the contract. Contact support.');
      case 'VALIDATION': {
        // The popup's field errors say it; a VALIDATION naming no popup field gets the generic line.
        const fields = fieldErrorsOf(error);
        if (fields.operator !== undefined || fields.signatory !== undefined) return null;
        break;
      }
      default:
        break;
    }
  }
  return t('Something went wrong. Please try again.');
};

/** The refusals that ask the owner to wait rather than report a failure (Alex, 2026-10-08). */
const WAIT_CODES: ReadonlySet<ContractErrorCode> = new Set<ContractErrorCode>([
  'RESEND_TOO_SOON',
  'DAILY_LIMIT',
  'DOCUMENT_PENDING',
]);

/** The Callout tone a notice shows in, on the section and in the popup: the informational tone for a
 *  wait, danger for any other refusal. */
export const contractNoticeTone = (error: ContractError | null): CalloutTone =>
  error !== null && WAIT_CODES.has(error.code) ? 'info' : 'danger';

export const fieldErrorText = (field: keyof NameErrors, code: NameErrorCode, t: TFunction): string => {
  if (code === 'too_long') {
    return t('Use at most {{max}} characters.', { max: field === 'operator' ? OPERATOR_NAME_MAX : SIGNATORY_NAME_MAX });
  }
  return field === 'operator' ? t('Enter the operator name.') : t('Enter the name of the signatory.');
};
